export type TierId = 'economy' | 'professional' | 'expert';

export interface TierDefinition {
    id: TierId;
    name: string;
    factor: number;
    quality: string;
    targetingProfile?: string;
    popular?: boolean;
    features: string[];
    color: string;
}

// NOTE: 'economy' tier is RETIRED from sale (no longer shown in purchase UI),
// but kept in PRICING_MATRIX so ~3,170 legacy economy projects keep calculating.
// Only Professional + Expert are offered to new buyers.
export const TIERS: TierDefinition[] = [
    {
        id: 'professional',
        name: 'Professional',
        factor: 1.0,
        quality: 'Residential IPs',
        targetingProfile: 'Residential Targeting Profile',
        popular: true,
        features: ['Residential IP Network', 'Higher Trust Traffic Quality', 'Country-Level Targeting', 'Credits Never Expire'],
        color: 'border-orange-300'
    },
    {
        id: 'expert',
        name: 'Expert',
        factor: 1.4,
        quality: 'Premium IPs',
        targetingProfile: 'Premium Targeting Profile',
        features: ['Premium Residential Network', 'GA4 Natural Events', 'City & State Targeting', 'Automatic Website Crawler'],
        color: 'border-[#ff5a0a]'
    }
];

// Legacy economy tier definition (NOT in TIERS — used only for rendering legacy projects)
export const LEGACY_ECONOMY: TierDefinition = {
    id: 'economy',
    name: 'Economy',
    factor: 0.5,
    quality: 'Datacenter IPs',
    targetingProfile: 'Country Targeting Profile',
    features: ['Datacenter IP Pool', 'High-Volume Delivery', 'Country-Level Targeting', 'Best Price per Visitor'],
    color: 'border-gray-200'
};

// New unified volume steps (9 tiers, round market-anchored numbers)
export const VOLUME_STEPS = [60000, 300000, 600000, 1000000, 3000000, 6000000, 10000000, 30000000, 60000000] as const;

// Legacy steps for old economy projects (do not use for new purchases)
export const LEGACY_VOLUME_STEPS = [70000, 600000, 1200000, 12000000, 60000000] as const;

export const BULK_OPTIONS = [1, 12, 24] as const;

export const MIN_VOLUME = VOLUME_STEPS[0];
export const MAX_VOLUME = VOLUME_STEPS[VOLUME_STEPS.length - 1];

export const PRICING_MATRIX: Record<TierId, Record<number, Record<number, number>>> = {
    // RETIRED from sale — kept for legacy economy projects only
    economy: {
        70000: { 1: 19.95, 12: 199.50, 24: 379.05 },
        600000: { 1: 57.95, 12: 579.50, 24: 1101.05 },
        1200000: { 1: 99.95, 12: 999.50, 24: 1899.05 },
        12000000: { 1: 699.95, 12: 6999.50, 24: 13299.05 },
        60000000: { 1: 2799.95, 12: 27999.50, 24: 53199.05 },
    },
    professional: {
        60000:    { 1: 19.95,   12: 199.50,    24: 379.05 },
        300000:   { 1: 59.95,   12: 599.50,    24: 1139.05 },
        600000:   { 1: 114.95,  12: 1149.50,   24: 2184.05 },
        1000000:  { 1: 189.95,  12: 1899.50,   24: 3609.05 },
        3000000:  { 1: 489.95,  12: 4899.50,   24: 9309.05 },
        6000000:  { 1: 849.95,  12: 8499.50,   24: 16149.05 },
        10000000: { 1: 1299.95, 12: 12999.50,  24: 24699.05 },
        30000000: { 1: 3299.95, 12: 32999.50,  24: 62699.05 },
        60000000: { 1: 5399.95, 12: 53999.50,  24: 102599.05 },
    },
    expert: {
        60000:    { 1: 27.95,   12: 279.50,    24: 531.05 },
        300000:   { 1: 83.95,   12: 839.50,    24: 1595.05 },
        600000:   { 1: 160.95,  12: 1609.50,   24: 3058.05 },
        1000000:  { 1: 265.95,  12: 2659.50,   24: 5053.05 },
        3000000:  { 1: 685.95,  12: 6859.50,   24: 13033.05 },
        6000000:  { 1: 1189.95, 12: 11899.50,  24: 22609.05 },
        10000000: { 1: 1819.95, 12: 18199.50,  24: 34579.05 },
        30000000: { 1: 4619.95, 12: 46199.50,  24: 87779.05 },
        60000000: { 1: 7559.95, 12: 75599.50,  24: 143639.05 },
    }
};

export const PUBLIC_STARTER_OFFER = Object.freeze({
    tier: 'professional' as const,
    volume: VOLUME_STEPS[0],
    price: PRICING_MATRIX.professional[VOLUME_STEPS[0]][1],
    currency: 'USD' as const,
    cpm: (PRICING_MATRIX.professional[VOLUME_STEPS[0]][1] / VOLUME_STEPS[0]) * 1000,
});

export const PUBLIC_MAX_VOLUME_SAVINGS_PERCENT = Math.round(
    (1 - (
        PRICING_MATRIX.professional[VOLUME_STEPS[0]][24]
        / (PRICING_MATRIX.professional[VOLUME_STEPS[0]][1] * 24)
    )) * 1000,
) / 10;

/**
 * Programmatic landing pages include older localized copy that predates the
 * current Professional/Expert matrix. Keep those pages indexable without ever
 * publishing retired Economy pricing or obsolete per-1,000 claims.
 */
/**
 * Das Ersatzangebot je Sprache.
 *
 * Warum das noetig ist: die Ersetzung unten lief bis 2026-08-22 immer mit dem
 * englischen Satz, auch auf lokalisierten Seiten. Ergebnis waren 42 Seiten mit
 * Saetzen wie „Ab $19.95 for 60,000 visitors." (de), „A partir de $19.95 for
 * 60,000 visitors." (fr) oder „Desde $19.95 for 60,000 visitors." (es) —
 * mitten in der Meta-Description, also genau in dem Text, den Google und die
 * Antwortmaschinen als Snippet zeigen.
 *
 * Preis und Volumen kommen weiter aus PUBLIC_STARTER_OFFER; hier steht nur die
 * Grammatik drumherum. Sprachen ohne Eintrag bekommen die neutrale Kurzform.
 */
const STARTER_OFFER_PHRASES: Record<string, (price: string, volume: string) => string> = {
    en: (p, v) => `$${p} for ${v} visitors`,
    de: (p, v) => `$${p} für ${v} Besuche`,
    fr: (p, v) => `${p} $ pour ${v} visites`,
    es: (p, v) => `$${p} por ${v} visitas`,
    pt: (p, v) => `$${p} por ${v} visitas`,
    it: (p, v) => `${p} $ per ${v} visite`,
    nl: (p, v) => `$${p} voor ${v} bezoeken`,
    sv: (p, v) => `${p} $ för ${v} besök`,
    pl: (p, v) => `${p} $ za ${v} wizyt`,
    cs: (p, v) => `${p} $ za ${v} návštěv`,
    ro: (p, v) => `${p} $ pentru ${v} vizite`,
    ru: (p, v) => `${p} $ за ${v} визитов`,
    tr: (p, v) => `${v} ziyaret için ${p} $`,
    id: (p, v) => `$${p} untuk ${v} kunjungan`,
    vi: (p, v) => `${p} $ cho ${v} lượt truy cập`,
    th: (p, v) => `${p} ดอลลาร์ สำหรับ ${v} การเข้าชม`,
    tl: (p, v) => `$${p} para sa ${v} pagbisita`,
    ja: (p, v) => `${v}アクセスで${p}ドル`,
    ko: (p, v) => `${v}회 방문에 ${p}달러`,
    zh: (p, v) => `${p} 美元 ${v} 次访问`,
    he: (p, v) => `$${p} עבור ${v} ביקורים`,
    ar: (p, v) => `$${p} مقابل ${v} زيارة`,
    hi: (p, v) => `${v} विज़िट के लिए $${p}`,
    bn: (p, v) => `${v} ভিজিটের জন্য $${p}`,
};

/** Zahlformat je Sprache — 60.000 gegen 60 000 gegen 60,000. */
const VOLUME_LOCALES: Record<string, string> = {
    de: 'de-DE', fr: 'fr-FR', es: 'es-ES', pt: 'pt-PT', it: 'it-IT', nl: 'nl-NL',
    sv: 'sv-SE', pl: 'pl-PL', cs: 'cs-CZ', ro: 'ro-RO', ru: 'ru-RU', tr: 'tr-TR',
    id: 'id-ID', vi: 'vi-VN', th: 'th-TH', tl: 'en-US', ja: 'ja-JP', ko: 'ko-KR',
    zh: 'zh-CN', he: 'he-IL', ar: 'en-US', hi: 'en-IN', bn: 'en-IN', en: 'en-US',
};

export const normalizePublicPricingCopy = (value: string, lang: string = 'en'): string => {
    const price = PUBLIC_STARTER_OFFER.price.toFixed(2);
    const volume = PUBLIC_STARTER_OFFER.volume.toLocaleString(VOLUME_LOCALES[lang] ?? 'en-US');
    const phrase = STARTER_OFFER_PHRASES[lang];
    // Ohne Eintrag bewusst wortlos: „$19.95 / 60,000" bleibt in jeder Sprache
    // lesbar, ein englischer Halbsatz nicht.
    const currentOffer = phrase ? phrase(price, volume) : `$${price} / ${volume}`;

    return value
        .replace(
            /(?:€\s*)?0[.,]17\s*(?:€|EUR|euros?|euro|евро|ユーロ|유로|欧元)?(?:\s*(?:CPM|\/\s*1(?:[,.\s]?000|k)))?/giu,
            currentOffer,
        )
        .replace(
            /(?:\$\s*)?2[.,]40\s*(?:\$|USD|dollars?|dollar|доллар(?:а|ов)?|ดอลลาร์|美元)?(?:\s*(?:\/|per|por|pro|par|за|每|ต่อ|cho)\s*1(?:[,.\s]?000|k))?/giu,
            currentOffer,
        )
        .replace(
            /(?:\$\s*)?1[.,]50\s*(?:\$|USD|dollars?|dollar|доллар(?:а|ов)?|ดอลลาร์|美元)?(?:\s*(?:\/|per|por|pro|par|за|每|ต่อ|cho)\s*1(?:[,.\s]?000|k))?/giu,
            currentOffer,
        )
        .replace(/Economy tier/giu, 'Professional tier')
        // Der Tarifname steht in lokalisierter Copy ohne das englische Wort
        // „tier" daneben — am 2026-08-22 auf sieben Seiten nachgewiesen
        // (ro „Nivelul Economy", cs „balíčkem Economy", tl „tier ng Economy").
        .replace(/\bEconomy\b/gu, 'Professional')
        .replace(/6-pack\s*\(20% off\)\s*and\s*24-pack\s*\(40% off\)\s*bulk discounts/giu, '12-pack and 24-pack volume options')
        .replace(/24-pack bulk discount/giu, '24-pack volume option')
        .replace(/(?:volume\s+)?discounts up to 40%/giu, `volume savings up to ${PUBLIC_MAX_VOLUME_SAVINGS_PERCENT}%`)
        .replace(/20\s*%\s*(?:-|–|—|~|to|bis|a|à|até|до|至|から|〜)\s*40\s*%/giu, `up to ${PUBLIC_MAX_VOLUME_SAVINGS_PERCENT}%`)
        .replace(/40\s*%/gu, `${PUBLIC_MAX_VOLUME_SAVINGS_PERCENT}%`)
        .replace(/\s{2,}/g, ' ')
        .trim();
};

// ANOMALY FIX: prices come from an EXACT lookup on defined steps — no runtime
// interpolation (the old anchorPrice*(v/anchor) caused e.g. 11M > 12M).
// Volume must be one of VOLUME_STEPS (slider snaps to them). Legacy economy
// volumes resolve against the economy matrix keys.
export const getTierPrice = (tierId: TierId, volume: number, bulk: number = 1): number => {
    const tierPrices = PRICING_MATRIX[tierId];
    if (!tierPrices) return 0;
    const step = tierPrices[volume];
    if (!step) return 0;
    return step[bulk] ?? step[1] ?? 0;
};

/* ────────────────────────────────────────────────────────────────────────────
   🔴 REGEL: DER REGLER RASTET NICHT EIN. Freie Mengen sind ausdruecklich
   gewollt (Ansage Martin, 2026-08-21) — wer 90.000 will, kauft 90.000.

   Diese Regel ist schon einmal rueckgaengig gemacht worden, deshalb steht hier
   die Vorgeschichte: Im Februar wurden die Zwischenmengen aus der Oberflaeche
   genommen und der Regler auf neun feste Stufen gerastet. Der Grund war echt —
   die damalige Backend-Formel skalierte vom naechsten Stufenwert NACH UNTEN
   hoch (`anchor_price * visitors / anchor`), und weil der Preis je Besucher mit
   der Menge faellt, kostete eine Zwischenmenge dadurch MEHR als die
   naechstgroessere Stufe: 290.000 ergaben $96.43, 300.000 nur $59.95. Der
   groesste Sprung lag bei 59,59 Mio. mit $1.198,85 Aufpreis fuers Wenigerkaufen.

   **Dieser Grund existiert nicht mehr.** `backend/routers/billing.py`
   interpoliert inzwischen **linear zwischen unterer und oberer Stufe**; der
   Preis ist damit ueber den gesamten Bereich monoton steigend, und auf einer
   definierten Stufe kommt exakt der hinterlegte Preis heraus. Das Einrasten
   war die Umgehung eines Fehlers, der behoben ist — es kostet seitdem nur noch
   Umsatz (belegt: WhatsApp-Anfrage vom 2026-08-18, 60.000 → 90.000 nicht
   kaufbar).

   ⚠️ **Wer das Einrasten wieder einbaut, muss zuerst zeigen, dass die
   Backend-Formel wieder nicht-monoton ist.** Ohne diesen Nachweis ist es eine
   Verschlechterung. Die Formel unten ist die **exakte Entsprechung** von
   `billing._calculate_package_price`; die beiden Tabellen wurden am 2026-08-21
   fuer `professional` und `expert` als wertgleich nachgewiesen. Weicht eine
   Seite ab, zeigt die Oberflaeche einen anderen Preis als abgebucht wird.
   ──────────────────────────────────────────────────────────────────────────── */

/** Kleinste kaufbare Schrittweite. `billing.py` lehnt alles ab, was nicht durch
 *  10.000 teilbar ist — der Regler darf also nichts anderes erzeugen. */
export const VOLUME_INCREMENT = 10000;

/**
 * Preis einer BELIEBIGEN Menge — exakte Entsprechung von
 * `billing._calculate_package_price`. Gibt `null` zurueck, wo auch das Backend
 * ablehnt; ein `0` waere hier gefaehrlich, weil es sich wie ein Preis liest.
 */
export const interpolateTierPrice = (
    tierId: TierId,
    volume: number,
    bulk: number = 1,
): number | null => {
    const tierPrices = PRICING_MATRIX[tierId];
    if (!tierPrices || volume <= 0) return null;
    const steps = Object.keys(tierPrices).map(Number).sort((a, b) => a - b);
    if (!steps.length) return null;
    if (volume < steps[0] || volume > steps[steps.length - 1]) return null;
    if (volume % VOLUME_INCREMENT !== 0) return null;

    const exact = tierPrices[volume]?.[bulk];
    if (exact !== undefined) return Number(exact.toFixed(2));

    const lower = Math.max(...steps.filter((step) => step <= volume));
    const upper = Math.min(...steps.filter((step) => step >= volume));
    const lowerPrice = tierPrices[lower]?.[bulk];
    const upperPrice = tierPrices[upper]?.[bulk];
    if (lowerPrice === undefined || upperPrice === undefined || upper === lower) return null;
    const share = (volume - lower) / (upper - lower);
    return Number((lowerPrice + (upperPrice - lowerPrice) * share).toFixed(2));
};

export const getCPM = (tierId: TierId, volume: number, bulk: number = 1): number => {
    const price = getTierPrice(tierId, volume, bulk);
    const totalVisitors = volume * bulk;
    if (totalVisitors === 0) return 0;
    return (price / totalVisitors) * 1000;
};

// Kept for backwards-compat; with snap-to-step it returns the exact volume.
export const getAnchorVolume = (volume: number): number => {
    let anchorVolume: number = MIN_VOLUME;
    for (const step of VOLUME_STEPS) {
        if (volume < step) break;
        anchorVolume = step;
    }
    return anchorVolume;
};

export const formatPrice = (price: number, currency: string = 'USD'): string => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
};

export const formatCPM = (cpm: number): string => {
    return `$${cpm.toFixed(2)}`;
};

export const formatVolume = (volume: number): string => {
    if (volume >= 1000000) {
        return `${(volume / 1000000).toFixed(volume % 1000000 === 0 ? 0 : 1)}M`;
    }
    if (volume >= 1000) {
        return `${(volume / 1000).toFixed(volume % 1000 === 0 ? 0 : 0)}k`;
    }
    return volume.toString();
};

export const SUBSCRIPTION_PLANS = [
    {
        id: 'ignite',
        name: 'Ignite',
        price: 19.99,
        yearlyPrice: 199.90, // 2 months free
        visitors: 25000,
        pageviews: 225000,
        priceId: 'price_ignite_monthly',
        yearlyPriceId: 'price_ignite_yearly',
        features: ['25,000 Guaranteed Visitors', 'Up to 225k Pageviews', 'Core Engine', 'Traffic Rewards']
    },
    {
        id: 'momentum',
        name: 'Momentum',
        price: 49.00,
        yearlyPrice: 490.00,
        visitors: 100000,
        pageviews: 1000000,
        priceId: 'price_momentum_monthly',
        yearlyPriceId: 'price_momentum_yearly',
        popular: true,
        features: ['100,000 Guaranteed Visitors', 'Up to 1m Pageviews', 'Core Engine', 'Traffic Rewards']
    },
    {
        id: 'breakthrough',
        name: 'Breakthrough',
        price: 99.00,
        yearlyPrice: 990.00,
        visitors: 300000,
        pageviews: 3000000,
        priceId: 'price_breakthrough_monthly',
        yearlyPriceId: 'price_breakthrough_yearly',
        features: ['300,000 Guaranteed Visitors', 'Up to 3m Pageviews', 'Core Engine', 'Traffic Rewards', 'Custom Reports']
    },
    {
        id: 'scale',
        name: 'Scale',
        price: 249.00,
        yearlyPrice: 2490.00,
        visitors: 1000000,
        pageviews: 15000000,
        priceId: 'price_scale_monthly',
        yearlyPriceId: 'price_scale_yearly',
        features: ['1m Guaranteed Visitors', 'Up to 15m Pageviews', 'Core Engine', 'Traffic Rewards', 'Custom Reports', 'API Access', 'Premium Geo Controls', 'Custom Events', 'Dedicated Account Manager']
    },
    {
        id: 'agency_pro',
        name: 'Agency Pro',
        price: 799.00,
        yearlyPrice: 7990.00,
        visitors: 10000000,
        pageviews: 150000000,
        priceId: 'price_agency_pro_monthly',
        yearlyPriceId: 'price_agency_pro_yearly',
        isAgency: true,
        features: ['10m Guaranteed Visitors', 'Up to 150m Pageviews', 'All Scale Features', 'Custom Distribution Pattern', 'Custom Feature Development']
    },
    {
        id: 'whitelabel_partner',
        name: 'Whitelabel Partner',
        price: 3000.00,
        yearlyPrice: 30000.00,
        visitors: 50000000,
        pageviews: 750000000,
        priceId: 'price_whitelabel_monthly',
        yearlyPriceId: 'price_whitelabel_yearly',
        isAgency: true,
        features: ['50m Guaranteed Visitors', 'Up to 750m Pageviews', 'All Agency Pro Features', 'On-request Whitelabel Website']
    }
];

export const CREDIT_PACKS = [
    { volume: 60000, label: '60k Visitors' },
    { volume: 300000, label: '300k Visitors' },
    { volume: 600000, label: '600k Visitors' },
    { volume: 1000000, label: '1M Visitors' },
    { volume: 3000000, label: '3M Visitors' },
    { volume: 6000000, label: '6M Visitors' },
    { volume: 10000000, label: '10M Visitors' },
    { volume: 30000000, label: '30M Visitors' },
    { volume: 60000000, label: '60M Visitors' }
];
